import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './AlterarSenha.css';

/**
 * Página para usuários alterarem sua própria senha
 */

const AlterarSenha = () => {
  const navigate = useNavigate();
  const { user, precisaAlterarSenha, clearPrecisaAlterarSenha } = useAuth();
  const [formData, setFormData] = useState({
    senha_atual: '',
    nova_senha: '',
    confirmar_senha: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Verificar se é obrigatório (via query param ou flag do contexto)
  const urlParams = new URLSearchParams(window.location.search);
  const obrigatorio = urlParams.get('obrigatorio') === 'true' || precisaAlterarSenha;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validações
    if (!formData.senha_atual || !formData.nova_senha || !formData.confirmar_senha) {
      setError('Todos os campos são obrigatórios');
      return;
    }

    // Validação de senha mais rigorosa (conforme backend)
    if (formData.nova_senha.length < 8) {
      setError('A nova senha deve ter pelo menos 8 caracteres');
      return;
    }
    
    // Verificar se tem pelo menos uma letra maiúscula, uma minúscula e um número
    const temMaiuscula = /[A-Z]/.test(formData.nova_senha);
    const temMinuscula = /[a-z]/.test(formData.nova_senha);
    const temNumero = /[0-9]/.test(formData.nova_senha);
    const temEspecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.nova_senha);
    
    if (!temMaiuscula || !temMinuscula || !temNumero || !temEspecial) {
      setError('A senha deve conter: pelo menos 8 caracteres, 1 maiúscula, 1 minúscula, 1 número e 1 caractere especial');
      return;
    }

    if (formData.nova_senha !== formData.confirmar_senha) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.senha_atual === formData.nova_senha) {
      setError('A nova senha deve ser diferente da senha atual');
      return;
    }

    setLoading(true);

    try {
      await apiClient.put('/alterar-senha', {
        senha_atual: formData.senha_atual,
        nova_senha: formData.nova_senha,
      });

      // Limpar flag precisa_alterar_senha após alteração bem-sucedida
      clearPrecisaAlterarSenha();

      setSuccess(true);
      setFormData({
        senha_atual: '',
        nova_senha: '',
        confirmar_senha: '',
      });

      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      
      // Tratar erros de validação do backend
      if (err.response?.data?.details?.erros) {
        const erros = err.response.data.details.erros;
        setError(erros.join(', '));
      } else {
        setError(err.response?.data?.error || 'Erro ao alterar senha');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>🔐 Alterar Senha</h1>
        {!obrigatorio && (
          <button className="btn" onClick={() => navigate('/dashboard')}>
            ← Voltar
          </button>
        )}
      </div>

      <div className="alterar-senha-card">
        {obrigatorio && (
          <div className="warning-message" style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffc107', 
            padding: '15px', 
            borderRadius: '8px', 
            marginBottom: '20px' 
          }}>
            <p><strong>⚠️ Você precisa alterar sua senha antes de continuar</strong></p>
            <p>Esta é uma medida de segurança. Por favor, defina uma nova senha para acessar o sistema.</p>
          </div>
        )}

        {success && (
          <div className="success-message">
            <p>✓ Senha alterada com sucesso!</p>
            <p>Redirecionando para o dashboard...</p>
          </div>
        )}

        {!success && (
          <>
            <div className="user-info">
              <p><strong>Usuário:</strong> {user?.nome}</p>
              <p><strong>Email:</strong> {user?.email}</p>
            </div>

            {error && <div className="error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Senha Atual *</label>
                <input
                  type="password"
                  name="senha_atual"
                  value={formData.senha_atual}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Digite sua senha atual"
                />
              </div>

              <div className="form-group">
                <label>Nova Senha *</label>
                <input
                  type="password"
                  name="nova_senha"
                  value={formData.nova_senha}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Mínimo 8 caracteres"
                  minLength={8}
                />
                <small>A senha deve ter pelo menos 8 caracteres, incluindo: 1 maiúscula, 1 minúscula, 1 número e 1 caractere especial</small>
              </div>

              <div className="form-group">
                <label>Confirmar Nova Senha *</label>
                <input
                  type="password"
                  name="confirmar_senha"
                  value={formData.confirmar_senha}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Digite a nova senha novamente"
                />
              </div>

              <div className="form-actions">
                {!obrigatorio && (
                  <button type="button" className="btn" onClick={() => navigate('/dashboard')} disabled={loading}>
                    Cancelar
                  </button>
                )}
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Alterando...' : 'Alterar Senha'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AlterarSenha;
